if (!customElements.get("td-combined-listing")) {
  let pendingRequests = new Map();
  let prefetchRequests = new Set();
  let prefetchCache = new Map();
  let ongoingFetches = new Set();
  
  class TdCombinedListing extends HTMLElement {
    constructor() {
      super();
      this.products = this.querySelectorAll('[data-product-handle]');
      this.isDataSavingMode = navigator?.connection?.saveData
      this.fetchSectionsTimeout = null;
      this.prefetchSectionsTimeout = null;
      this.sections = document.querySelectorAll('[data-combined-listing]');
      this.productData = null;
      this.selectedOptions = null;

    }

    connectedCallback() {
      const productData = document.querySelector('[data-product-data]');
      if (productData) {
        try {
          this.productData = JSON.parse(productData.textContent);
        } catch (error) {
          console.error('Error parsing product data:', error);
        }
      }
      
      if (this.products) {
        this.products.forEach(prod => {
          prod.addEventListener('mouseover', () => {
            const productHandle = prod.dataset.productHandle;
            const variantId = prod.dataset.variantId;
            this.sections.forEach((section) => {
              const section_id = section.dataset.sectionId;
              this.prefetchSection(section_id, `/products/${productHandle}`, variantId);
            })
          });
          prod.addEventListener('click', () => {
            const productHandle = prod.dataset.productHandle;
            const variantId = prod.dataset.variantId;
            this.updateUrl(productHandle,variantId);
            this.sections.forEach((section) => {
              const section_id = section.dataset.sectionId;
              this.loadSection(section_id, `/products/${productHandle}`, variantId).then(
                (content) => {
                  this.updateSectionContent(section_id, content, prod.dataset.productHandle);
                }
              );
            });
          });
        })
        document.addEventListener('tdVariantChange', this.handleVariantChange.bind(this)); 
        window.addEventListener('popstate', function() {
          if (history.state && history.state.url === location.href) {
            location.reload();
          }
        });
      }
    }
    disconnectedCallback() {
      document.removeEventListener('tdVariantChange', this.handleVariantChange.bind(this));
      window.removeEventListener('popstate', function() {
        if (history.state && history.state.url === location.href) {
          location.reload();
        }
      });
    }
    handleVariantChange(e) {
      // TD CHANGE - Fade/cross out color swatches that are out of stock in the CURRENTLY SELECTED size.
      // Until a size is chosen, no swatch is faded (avoids the "everything looks sold out" state).
      // Also keeps each swatch's target variant in sync so switching color preserves the chosen size.
      if (!this.productData) return;
      const selectedVariant = e.detail && e.detail.variant;
      const disabledClass = 'td-product__form__option--disabled';
      const optionKeys = ['option1', 'option2', 'option3'];
      const optionComparison = (variant1, variant2) => {
        return optionKeys.every(key => variant1[key] === variant2[key]);
      }
      this.productData.forEach(product => {
        const productElement = this.querySelector(`.td-product__form__option[data-product-handle='${product.handle}']`);
        if (!productElement) return;

        // No size selected yet → nothing to evaluate against, show the swatch as available.
        if (!selectedVariant) {
          productElement.classList.remove(disabledClass);
          return;
        }

        const matchingVariant = product.variants.find(variant => optionComparison(variant, selectedVariant));
        // Available only if this color offers the selected size AND it's in stock.
        if (matchingVariant && matchingVariant.available) {
          productElement.classList.remove(disabledClass);
        } else {
          productElement.classList.add(disabledClass);
        }

        // Keep the swatch's target variant in sync (preserve size across colors; fall back if absent).
        if (matchingVariant) {
          productElement.dataset.variantId = matchingVariant.id;
        } else if (product.selected_or_first_available_variant_id) {
          productElement.dataset.variantId = product.selected_or_first_available_variant_id;
        }
      });
      // END TD CHANGE
    }
    buildCacheKey(sectionId, path, variantId) {
      return `${path}:${sectionId}${variantId ? `:variant:${variantId}` : ''}`;
    }
    
    buildRequestGroups(sectionKeys) {
      const groups = sectionKeys.reduce(
        (acc, key) => {
          const [path, sectionId, , variantId] = key.split(':')
          const groupKey = `${path || ''}:${variantId || ''}`
    
          if (!acc[groupKey]) {
            acc[groupKey] = {
              path,
              variantId,
              keys: [],
              sectionIds: [],
            }
          }
          acc[groupKey].keys.push(key)
          acc[groupKey].sectionIds.push(sectionId)
          return acc
        },
        {}
      )
    
      return Object.values(groups)
    }
    
    scheduleFetchSections() {
      if (this.fetchSectionsTimeout) {
        clearTimeout(this.fetchSectionsTimeout)
      }
      this.fetchSectionsTimeout = setTimeout(() => this.fetchSections(), 100)
    }
    
    schedulePrefetchSections() {
      if (this.prefetchSectionsTimeout) {
        clearTimeout(this.prefetchSectionsTimeout)
      }
      this.prefetchSectionsTimeout = setTimeout(() => this.prefetchSections(), 200)
    }
    
    async fetchSections() {
      if (pendingRequests.size === 0) {
        return
      }
    
      const sectionKeys = Array.from(pendingRequests.keys())
      const requestGroups = this.buildRequestGroups(sectionKeys)
    
      try {
        await Promise.all(
          requestGroups.map(async ({ path, variantId, keys, sectionIds }) => {
            const url = new URL(window.location.href)
            if (path) {
              url.pathname = path
            }
            url.searchParams.set('sections', sectionIds.join(','))
            if (variantId) {
              url.searchParams.set('variant', variantId)
            }
            const requestUrl = url.toString()
    
            const response = await fetch(requestUrl)
            if (!response.ok) {
              throw new Error(`Failed to fetch: ${requestUrl}`)
            }
            const sectionsData = await response.json()
    
            keys.forEach((key, index) => {
              const callbacks = pendingRequests.get(key) || []
              pendingRequests.delete(key)
              const sectionId = sectionIds[index]
              const data = sectionsData[sectionId]
              if (data === undefined) {
                callbacks.forEach(({ reject }) => reject(new Error(`No data for section ${sectionId}`)))
              } else {
                callbacks.forEach(({ resolve }) => resolve(data))
                prefetchCache.set(key, data)
              }
              ongoingFetches.delete(key)
            })
          }),
        )
      } catch (error) {
        console.error(error)
        const sectionKeys = Array.from(pendingRequests.keys())
        sectionKeys.forEach((key) => {
          const callbacks = pendingRequests.get(key) || []
          pendingRequests.delete(key)
          callbacks.forEach(({ reject }) => reject(error))
          ongoingFetches.delete(key)
        })
      }
    }
    
    async prefetchSections() {
      if (prefetchRequests.size === 0) {
        return
      }
    
      const sectionKeys = Array.from(prefetchRequests)
      const requestGroups = this.buildRequestGroups(sectionKeys)
    
      try {
        await Promise.all(
          requestGroups.map(async ({ path, variantId, keys, sectionIds }) => {
            const url = new URL(window.location.href)
            if (path) {
              url.pathname = path
            }
            url.searchParams.set('sections', sectionIds.join(','))
            if (variantId) {
              url.searchParams.set('variant', variantId)
            }
            const requestUrl = url.toString()
    
            const response = await fetch(requestUrl)
            if (!response.ok) {
              throw new Error(`Failed to prefetch: ${requestUrl}`)
            }
            const sectionsData = await response.json()
    
            keys.forEach((key, index) => {
              const sectionId = sectionIds[index]
              const data = sectionsData[sectionId]
              if (data !== undefined) {
                prefetchCache.set(key, data)
              }
              prefetchRequests.delete(key)
              ongoingFetches.delete(key)
    
              const callbacks = pendingRequests.get(key) || []
              pendingRequests.delete(key)
              callbacks.forEach(({ resolve, reject }) => {
                if (data !== undefined) {
                  resolve(data)
                } else {
                  reject(new Error(`No data for section ${sectionId}`))
                }
              })
            })
          }),
        )
      } catch (error) {
        console.error(error)
        const sectionKeys = Array.from(prefetchRequests)
        sectionKeys.forEach((key) => {
          prefetchRequests.delete(key)
          ongoingFetches.delete(key)
          const callbacks = pendingRequests.get(key) || []
          pendingRequests.delete(key)
          callbacks.forEach(({ reject }) => reject(error))
        })
      }
    }
    
    loadSection(sectionId, path = '', variantId) {
      const key = this.buildCacheKey(sectionId, path, variantId)
    
      if (prefetchCache.has(key)) {
        return Promise.resolve(prefetchCache.get(key))
      }
    
      return new Promise((resolve, reject) => {
        if (!pendingRequests.has(key)) {
          pendingRequests.set(key, [])
        }
        pendingRequests.get(key)?.push({ resolve, reject })
    
        if (!ongoingFetches.has(key) && !prefetchRequests.has(key)) {
          ongoingFetches.add(key)
          this.scheduleFetchSections()
        }
      })
    }
    
    prefetchSection(sectionId, path = '', variantId) {
      if (this.isDataSavingMode) {
        return
      }
    
      const key = this.buildCacheKey(sectionId, path, variantId)
    
      if (pendingRequests.has(key) || ongoingFetches.has(key) || prefetchCache.has(key)) {
        return
      }
    
      if (!prefetchRequests.has(key)) {
        prefetchRequests.add(key)
        this.schedulePrefetchSections()
      }
    }
    
    cancelPrefetchSection(sectionId, path = '', variantId) {
      const key = this.buildCacheKey(sectionId, path, variantId)
      prefetchRequests.delete(key)
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    /**
     * Updates section content with a smooth fade transition to prevent flashing
     * @param {string} sectionId - The section ID to update
     * @param {string} content - The HTML content to insert
     */
    updateSectionContent(sectionId, content, productHandle) {
      const sectionElement = document.querySelector(`[data-section-id='${sectionId}']`);
      if (!sectionElement) return;

      // Add transition class if not already present
      if (!sectionElement.style.transition) {
        sectionElement.style.transition = 'opacity 0.15s ease-in-out';
      }

      // Fade out
      sectionElement.style.opacity = '0';

      // Wait for fade out, then replace content and fade in
      requestAnimationFrame(() => {
        setTimeout(() => {
          sectionElement.innerHTML = this.getSectionInnerHTML(content, `[data-section-id='${sectionId}']`);
          sectionElement.dispatchEvent(new CustomEvent("section-content::changed", { bubbles: true, detail: sectionId }));
          this.maybeRunAdditionalActions(sectionId, productHandle);

          // Fade in
          requestAnimationFrame(() => {
            sectionElement.style.opacity = '1';
          });
        }, 150);
      });
    }

    updateUrl(productHandle, variantId) {
      let newUrl = `/products/${productHandle}`;
      const currentUrl = new URL(window.location.href);
      const currentParams = new URLSearchParams(currentUrl.search);
    
      currentParams.forEach((value, key) => {
        if (key === 'variant' || key === 'selling_plan') {
          currentParams.delete(key);
        }
      });

      if (variantId) {
        currentParams.set('variant', variantId);
      }
    
      const finalUrl = `${currentUrl.origin}${newUrl}?${currentParams.toString()}`;
    
      history.replaceState({ url: finalUrl }, null, finalUrl);
    }
    maybeRunAdditionalActions(sectionId, productHandle) {
      this.maybeInitializeSwymButtons(sectionId);
      this.maybeReinitializeRestockConfig(productHandle);
    }
    maybeInitializeSwymButtons(sectionId) {
      const selector = `[data-section-id='${sectionId}'] .swym-button-bar`;
      if (window._swat && document.querySelector(selector)) {
        window._swat.initializeActionButtons(selector);
      }
    }
    maybeReinitializeRestockConfig(prodHandle) {
      const matchingProduct = this.productData.find(product => product.handle === prodHandle);
      if (matchingProduct && window._ReStockConfig) {
        window._ReStockConfig.product = matchingProduct;
        
        // Remove the SDK script if it exists
        const existingScript = document.querySelectorAll('script[src*="restock-alerts-sdk"]');
        const scriptSrc = existingScript[0]?.src;
        existingScript.forEach(script => script.remove());
        
        // Remove the loaded flag
        delete document.documentElement.dataset.restockAlertsStatus;
        
        // Remove any SDK-injected elements (buttons, forms, etc.)
        const sdkElements = document.querySelectorAll('[class*="nm-"], [id*="restock"], [id*="notify-me"]');
        sdkElements.forEach(el => el.remove());
        
        // Remove the root element the SDK might inject
        const sdkRoot = document.getElementById('restock-global-css');
        if (sdkRoot) sdkRoot.remove();
        
        // Clear any cached SDK state
        if (window.HengamRestock) {
          delete window.HengamRestock;
        }
        
        // Wait a moment, then reload the SDK
        setTimeout(() => {
          if (!scriptSrc) return;

          const restockScript = document.createElement('script');
          restockScript.src = scriptSrc;
          restockScript.async = true;
          document.body.appendChild(restockScript);
        }, 100);
      }
    }
  }



  customElements.define("td-combined-listing", TdCombinedListing);
}
